import MileageListingFilter, {
  MileageListingCardNumberFilter,
  MileageListingMileageDateFilter,
  MileageListingDistanceFilter,
  MileageListingEndAddressFilter,
  MileageListingHumanIdFilter,
  MileageListingMileageNumberFilter,
  MileageListingStartAddressFilter,
  MileageListingWorkerNameFilter,
  MileageListingCargoCompanyNameFilter,
  MileageListingVehicleTypeFilter,
  MileageListingStateFilter,
} from "./types/mileage-listing-filter";
import MileageListingFilterType from "./types/mileage-listing-filter-type";
import MileageListingSortKey from "./types/mileage-listing-sort-key";
import MileageListingVehicleType from "./types/mileage-listing-vehicle-type";
import MileageListingStatus from "./types/mileage-listing-status";
import MileageDownloadAllCSVParams, {
  MileageDownloadAllCSVParamsOrder,
  MileageDownloadAllCSVParamsState,
  MileageDownloadAllCSVParamsVehicleType,
} from "../../../../common/services/mileage/download-all-csv/mileage-download-all-csv-params";

const createVehicleType = (
  type: MileageListingVehicleType | undefined,
): MileageDownloadAllCSVParamsVehicleType | undefined => {
  switch (type) {
    case MileageListingVehicleType.CAR_GT_900CM3:
      return MileageDownloadAllCSVParamsVehicleType.CAR_GT_900CM3;
    case MileageListingVehicleType.CAR_LE_900CM3:
      return MileageDownloadAllCSVParamsVehicleType.CAR_LE_900CM3;
    case MileageListingVehicleType.COMPANY_OWNED:
      return MileageDownloadAllCSVParamsVehicleType.COMPANY_OWNED;
    case MileageListingVehicleType.MOPED:
      return MileageDownloadAllCSVParamsVehicleType.MOPED;
    case MileageListingVehicleType.MOTORCYCLE:
      return MileageDownloadAllCSVParamsVehicleType.MOTORCYCLE;
    default:
      return undefined;
  }
};

const createOrder = (
  sortKey: MileageListingSortKey | null,
): MileageDownloadAllCSVParamsOrder | undefined => {
  switch (sortKey) {
    case MileageListingSortKey.CARD_NO_ASC:
      return MileageDownloadAllCSVParamsOrder.CARD_NO_ASC;
    case MileageListingSortKey.CARD_NO_DESC:
      return MileageDownloadAllCSVParamsOrder.CARD_NO_DESC;
    case MileageListingSortKey.DATE_ASC:
      return MileageDownloadAllCSVParamsOrder.DATE_ASC;
    case MileageListingSortKey.DATE_DESC:
      return MileageDownloadAllCSVParamsOrder.DATE_DESC;
    case MileageListingSortKey.DISTANCE_ASC:
      return MileageDownloadAllCSVParamsOrder.DISTANCE_ASC;
    case MileageListingSortKey.DISTANCE_DESC:
      return MileageDownloadAllCSVParamsOrder.DISTANCE_DESC;
    case MileageListingSortKey.HUMAN_ID_ASC:
      return MileageDownloadAllCSVParamsOrder.HUMAN_ID_ASC;
    case MileageListingSortKey.HUMAN_ID_DESC:
      return MileageDownloadAllCSVParamsOrder.HUMAN_ID_DESC;
    case MileageListingSortKey.WORKER_NAME_ASC:
      return MileageDownloadAllCSVParamsOrder.WORKER_NAME_ASC;
    case MileageListingSortKey.WORKER_NAME_DESC:
      return MileageDownloadAllCSVParamsOrder.WORKER_NAME_DESC;
    case MileageListingSortKey.CARGO_COMPANY_NAME_ASC:
      return MileageDownloadAllCSVParamsOrder.CARGO_COMPANY_NAME_ASC;
    case MileageListingSortKey.CARGO_COMPANY_NAME_DESC:
      return MileageDownloadAllCSVParamsOrder.CARGO_COMPANY_NAME_DESC;
    case MileageListingSortKey.WORKER_LAST_NAME_ASC:
      return MileageDownloadAllCSVParamsOrder.WORKER_LAST_NAME_ASC;
    case MileageListingSortKey.WORKER_LAST_NAME_DESC:
      return MileageDownloadAllCSVParamsOrder.WORKER_LAST_NAME_DESC;
    default:
      return undefined;
  }
};

const createState = (
  type: MileageListingStatus | undefined,
): MileageDownloadAllCSVParamsState | undefined => {
  switch (type) {
    case MileageListingStatus.PENDING:
      return MileageDownloadAllCSVParamsState.PENDING;
    case MileageListingStatus.ACCEPTED:
      return MileageDownloadAllCSVParamsState.ACCEPTED;
    case MileageListingStatus.REJECTED:
      return MileageDownloadAllCSVParamsState.REJECTED;
    case MileageListingStatus.DELEGATION_CREATED:
      return MileageDownloadAllCSVParamsState.DELEGATION_CREATED;
    default:
      return undefined;
  }
};

const create = (
  filters: MileageListingFilter[],
  sortKey: MileageListingSortKey | null,
): MileageDownloadAllCSVParams => {
  const dateFilter = filters.find(
    (filter) => filter.type == MileageListingFilterType.MILEAGE_DATE,
  )?.value as MileageListingMileageDateFilter["value"] | undefined;

  const distanceFilter = filters.find(
    (filter) => filter.type == MileageListingFilterType.DISTANCE,
  )?.value as MileageListingDistanceFilter["value"] | undefined;

  const startAddress = filters.find(
    (filter) => filter.type == MileageListingFilterType.START_ADDRESS,
  )?.value as MileageListingStartAddressFilter["value"] | undefined;

  const endAddress = filters.find(
    (filter) => filter.type == MileageListingFilterType.END_ADDRESS,
  )?.value as MileageListingEndAddressFilter["value"] | undefined;

  const vehicleType = filters.find(
    (filter) => filter.type == MileageListingFilterType.VEHICLE_TYPE,
  )?.value as MileageListingVehicleTypeFilter["value"] | undefined;

  const state = filters.find(
    (filter) => filter.type == MileageListingFilterType.STATE,
  )?.value as MileageListingStateFilter["value"] | undefined;

  return {
    order: createOrder(sortKey),
    dateSince: dateFilter?.from,
    dateTo: dateFilter?.to,
    humanId: filters.find(
      (filter) => filter.type == MileageListingFilterType.HUMAN_ID,
    )?.value as MileageListingHumanIdFilter["value"] | undefined,
    mileageNumber: filters.find(
      (filter) => filter.type == MileageListingFilterType.MILEAGE_NUMBER,
    )?.value as MileageListingMileageNumberFilter["value"] | undefined,
    workerName: filters.find(
      (filter) => filter.type == MileageListingFilterType.WORKER_NAME,
    )?.value as MileageListingWorkerNameFilter["value"] | undefined,
    cardNumber: filters.find(
      (filter) => filter.type == MileageListingFilterType.CARD_NUMBER,
    )?.value as MileageListingCardNumberFilter["value"] | undefined,
    cargoCompanyName: filters.find(
      (filter) => filter.type == MileageListingFilterType.CARGO_COMPANY_NAME,
    )?.value as MileageListingCargoCompanyNameFilter["value"] | undefined,
    vehicleType: createVehicleType(vehicleType),
    state: createState(state),
    startAddress: startAddress ? [startAddress] : undefined,
    endAddress: endAddress ? [endAddress] : undefined,
    distanceMin: distanceFilter?.from,
    distanceMax: distanceFilter?.to,
  };
};

const mileageListingDownloadAllCSVParamsFactory = {
  create,
};

export default mileageListingDownloadAllCSVParamsFactory;
